/* eslint-disable no-unused-vars */
import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  useNavigate,
  useLocation,
  Outlet,
  useParams,
  useMatch,
} from 'react-router-dom';

import { setSchoolCode } from 'slice/global';
import { getSchoolInfoAction } from 'slice/main';

import { isEmpty } from 'lodash';

const langComparison = {
  zh: 'zh',
  english: 'en',
  cn: 'cn',
};

const navigatorLangTransition = {
  'zh-TW': 'zh',
  'zh-CN': 'cn',
  'en-US': 'english',
};

const RedirectGuard = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  const { lang } = useParams();

  const { info } = useSelector((state) => state.main);
  const { schoolCode } = useSelector((state) => state.global);
  const { updateMode, readMode } = useSelector((state) => state.step);

  const matchs = [
    useMatch('/:lang/token/:token'),
    useMatch('/:lang/applications/payment'),
    useMatch('/:lang/applications/:payment_status'),
    useMatch('/:lang/accept-result'),
    // useMatch('/:lang/form_task/:display_id'),
    // useMatch('/:lang/form_task/:display_id/confirmation'),
    useMatch('/:lang/interview'),
    useMatch('/:lang/profile/detail')
  ];

  const getSupportLang = ($info) => {
    if (isEmpty($info?.support_lang)) {
      return [];
    }
    return $info.support_lang.reduce(
      (supportLang, current) =>
        !langComparison[current]
          ? supportLang
          : { ...supportLang, [current]: langComparison[current] },
      {}
    );
  };
  const languageSupport = useCallback(getSupportLang(info), [info]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (schoolCode) {
      dispatch(
        getSchoolInfoAction({
          schoolCode,
        })
      );
    }
  }, [schoolCode, lang]);

  useEffect(() => {
    const url = window.location.hostname;
    const urlregex =
      /^(prepro-ns-(.*)|(.*)-admission|((.*)\.admission))\.d?ekinder\..*$/;

    const $urlPath = (url.match(urlregex) || []).filter((val) => !!val);
    let $schoolCode;

    try {
      $schoolCode = $urlPath.at(-1);
    } catch (err) {
      $schoolCode = ($urlPath || [undefined]).pop();
    }

    if (!$schoolCode) {
      const addtionalRegexp = /^(.*?)\.(dekinder\.hk|cloudoase\.com)/;
      const addtionalUrlPath = url.match(addtionalRegexp);
      try {
        $schoolCode = addtionalUrlPath.at(-2);
      } catch (error) {
        $schoolCode = (addtionalUrlPath || [undefined]).pop();
      }
    }

    if ($schoolCode) {
      dispatch(setSchoolCode($schoolCode));
    } else {
      dispatch(setSchoolCode(process.env.REACT_APP_SCHOOL_CODE));
    }
  }, []);

  // 多语言校验
  /**
   * 1. filter with school info support language
   * 2. block direct language path if not support -> redirect to default_lang or first language on list
   */
  useEffect(() => {
    const supportLangKey = Object.keys(languageSupport) || [];
    const supportLangValues = Object.values(languageSupport) || [];
    if (supportLangKey.length > 0) {
      if (supportLangValues.indexOf(lang) === -1) {
        i18n.changeLanguage(info?.default_lang || supportLangKey[0]);
        navigate(
          `/${languageSupport[info?.default_lang] || supportLangValues[0]}`
        );
      } else {
        const validPath = matchs.reduce(
          (result, current) => (current !== null ? true : result),
          false
        );
        // 暂时修复；更改模式和只读模式跳过
        if (!validPath && !updateMode && !readMode) {
          navigate(`/${lang}`, { replace: true });
        }
      }
    } else {
      const langStr = Object.keys(langComparison).find(
        (key) => langComparison[key] === lang
      );
      if (langStr) {
        i18n.changeLanguage(langStr);
      }
    }
  }, [lang, languageSupport]);

  /**
   * Handling first route for langauage,
   * Wait for school info callback
   * get school info default -> get browser language -> use i18n default
   * only affected at '/' url
   */
  useEffect(() => {
    if (pathname === '/' && !isEmpty(info)) {
      const navigatorLang = window.navigator.language;
      const navLang =
        Object.keys(navigatorLangTransition).indexOf(navigatorLang) > -1
          ? navigatorLangTransition[navigatorLang]
          : undefined;
      const langStr =
        languageSupport[info?.default_lang || navLang || i18n.language];
      if (langStr !== undefined) {
        i18n.changeLanguage(info?.default_lang || navLang || i18n.language);
        navigate(
          `/${languageSupport[info?.default_lang || navLang || i18n.language]}`
        );
      } else {
        const defaultLang = Object.keys(languageSupport)[0];
        i18n.changeLanguage(defaultLang);
        navigate(`/${languageSupport[defaultLang]}`);
      }
    }
    return () => {};
  }, [pathname, info]);

  return <Outlet />;
};

export default RedirectGuard;
